<template>
  <div class="flex w-70 max-w-full flex-col gap-2 px-1 text-body text-text">
    <div class="text-center font-medium">{{ $t(`menu.invite_to_project`) }}</div>
    <SingleLineInput
      v-model="searchInput"
      placeholder='user@example.com'
      initialyFocused
      @keyup-enter="doInvite"/>
    <div
      v-if='searchedUsers.length'
      class="v-tracklify-scroll flex max-h-32 flex-col gap-1 overflow-y-auto">
      <div
        class='flex cursor-pointer items-center gap-2 rounded-chip px-1.5 py-1
               transition-colors duration-150 hover:bg-hover'
        @click='actionInviteUserToProject({projectId: project.id, user})
          && close()'
        :key='`inv-member-${user.id}`' v-for='user in searchedUsers'>
        <UserAvatar withDayoffBadge :user='user'/>
        <div class="min-w-0 flex-1 truncate">
          {{ nameParts(user.username).before }}<strong>{{ nameParts(user.username).match }}</strong>{{ nameParts(user.username).after }}
        </div>
      </div>
    </div>
    <div v-if='!!searchedUsers.length' :class="FOOTER_NOTE">
      {{ $t('menu.user_invite') }}
    </div>
    <div v-else-if='isEmail' class="flex flex-col gap-2.5">
      <div v-if='inviteError' class="text-center text-label text-red">{{ inviteError }}</div>
      <template v-else-if='!registered'>
        <div>
          {{ $t(`search.invite`)}} <strong>{{ searchInput }}</strong> {{
             $t(`search.invite_to_project`) }} <strong>{{
            project.name }}</strong>
        </div>
        <SimpleButton
          size="lg"
          variant="accent"
          class="w-full"
          @click="doInvite"
          :text="$t(`search.invite_user`)"/>
      </template>

      <template v-else-if='!registered.registrationCompleted && !registered.approved'>
        <div>
          {{ $t('menu.Resend_invite') }} <strong>{{ searchInput }}</strong>
        </div>
        <SimpleButton
          size="lg"
          variant="accent"
          class="w-full"
          @click="doInvite"
          :text="$t(`menu.resend_invite_button`)"/>
      </template>

      <div v-else :class="FOOTER_NOTE">
        {{$t('menu.already_member_project')}}
      </div>
    </div>
    <div v-else :class="FOOTER_NOTE">
      {{ $t(`menu.no_users_found`) }}
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import validator from 'validator';
import SingleLineInput from '@/components/elements/SingleLineInput';
import SimpleButton from '@/components/elements/SimpleButton';

const FOOTER_NOTE = `mt-2 border-t-[0.5px] border-muted/50 pt-2 text-center
  text-label text-text-muted`;

const emit = defineEmits(['close', 'infoAfterInvite']);

const { t } = useI18n();
const store = useStore();

const workspace = computed(() => store.state.user.workspace);
const project = computed(() => store.state.project.project);
const getUser = (id) => store.getters['user/getUser'](id);

const searchInput = ref('');
// back refused the invite: said instead of the button until the input changes
const inviteError = ref('');

// emails in any case are one account: compared and sent lower-cased
const email = computed(() => searchInput.value.trim().toLowerCase());
const isEmail = computed(() => validator.isEmail(email.value));

const registered = computed(() => project.value.projectMembers.find(
  p => getUser(p.userId).email?.toLowerCase() === email.value,
));

const searchedUsers = computed(() => {
  if (!workspace.value) {
    return [];
  }
  const users = [...workspace.value.users];
  return users.filter(
    u => (!u.botClassId // bots are workspace members, but nobody invites them anywhere
      && (u.username
      && (u.username.toLowerCase().includes(searchInput.value.toLowerCase())
      || u.email?.toLowerCase().includes(email.value)))
      && project.value.projectMembers.findIndex(pm => pm.userId === u.id) === -1
    ),
  ).sort((a, b) => {
    if (!a.username.includes('@') && b.username.includes('@')) {
      return -1;
    }
    if (a.username.includes('@') && !b.username.includes('@')) {
      return 1;
    }
    if (a.username.toLowerCase() < b.username.toLowerCase()) { return -1; }
    if (a.username.toLowerCase() > b.username.toLowerCase()) { return 1; }
    return 0;
  });
});

watch(searchInput, () => {
  inviteError.value = '';
});

// the name split around the searched text (shown in bold); rendered as text, never as HTML
const nameParts = (name) => {
  const at = searchInput.value ? name.indexOf(searchInput.value) : -1;
  if (at === -1) {
    return { before: name, match: '', after: '' };
  }
  return {
    before: name.slice(0, at),
    match: searchInput.value,
    after: name.slice(at + searchInput.value.length),
  };
};

const actionInviteUserToProject = (payload) => store.dispatch('project/actionInviteUserToProject', payload);
const actionReinviteUser = (payload) => store.dispatch('project/actionReinviteUser', payload);

const close = () => {
  emit('close');
};

const doInvite = async () => {
  let result;
  if (!searchedUsers.value.length && isEmail.value && !registered.value) {
    result = await actionInviteUserToProject({
      projectId: project.value.id,
      email: email.value,
    });
  } else if (registered.value && !registered.value.registrationCompleted) {
    result = await actionReinviteUser({
      id: registered.value.userId,
      pmId: registered.value.id,
      projectId: project.value.id,
      user: getUser(registered.value.userId),
    });
  } else {
    return;
  }
  if (result?.error) {
    console.error('Error inviting a user:', result.error);
    inviteError.value = t('menu.invite_failed', { email: email.value });
    return;
  }
  close();
  emit('infoAfterInvite', email.value);
};
</script>

